import { FormField } from 'src/core/forms/FormField';
import { useMessageContext } from 'src/shared/support/MessageContext';
import { useEffect, useRef, useState } from 'react';
import type { AxiosRequestConfig } from 'axios';
import { Box, Tooltip } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import PrintIcon from '@mui/icons-material/Print';
import CustomButton from 'src/core/ui/CustomButton';
import useRequest from 'src/core/api/useRequest';
import { useSettingsContext } from 'src/core/settings/useSettings';
import { printRelocationOrder } from 'src/core/api/apiRequests';
import { printDispatchPdf, useDispatchPrintingSettings } from 'src/shared/printing/PrintingManager';
import type { DispatchPrintingSettings, PrintOutcome } from 'src/shared/printing/PrintingManager';

// Keep this hook on the page, above stations that remount after a save.
export const useRelocationOrderPrinting = (page: 'relocateProducts' | 'relocateReceivedProducts') => {
    const { settings, setSetting } = useSettingsContext();
    const printingSettings = useDispatchPrintingSettings();
    const autoPrintKey = `${page}AutoPrintRelocationOrder`;
    const lastOrderKey = `${page}LastRelocationOrder`;
    const autoPrint = settings?.[autoPrintKey] === true || settings?.[autoPrintKey] === 'true';
    const storedOrder = settings?.[lastOrderKey];
    const lastOrder: RelocationOrder | null = hasIdentifier(storedOrder?.orderId) && hasIdentifier(storedOrder?.warehouseId) ? storedOrder : null;
    const [jobs, setJobs] = useState<RelocationPrintJob[]>([]);
    const { updateMessageStatus } = useMessageContext();
    const notify = (notice: { severity: 'warning' | 'error'; message: string } | null) => { if (notice) updateMessageStatus({ requestUrl: '/printing/relocation-order', status: notice.severity === 'error' ? 'error' : 'success', message: notice.message }); };
    const sequence = useRef(0);
    const pending = useRef(0);
    const job = jobs[0];
    const enqueue = (order: RelocationOrder) => {
        const next = { ...order, key: ++sequence.current, settings: printingSettings };
        pending.current += 1;
        setJobs((current) => [...current, next]);
    };
    const onRelocated = (response: any, warehouseId: Identifier) => {
        const data = response?.data ?? response;
        // One response represents one order, regardless of its number of rows.
        if (!hasIdentifier(data?.OrderId)) {
            notify({ severity: 'warning', message: 'Relocation saved, but no order ID was returned. The last printable relocation order has been kept.' });
            return;
        }
        const order = { orderId: data.OrderId, warehouseId };
        setSetting(lastOrderKey, order);
        if (autoPrint) enqueue(order);
    };
    const printLast = () => {
        if (lastOrder && pending.current === 0) enqueue(lastOrder);
    };
    const complete = (result: RelocationPrintResult) => {
        if (!job) return;
        pending.current = Math.max(0, pending.current - 1);
        setJobs((current) => current.filter((item) => item.key !== job.key));
        const outcome = result.outcome;
        if (result.error) notify({ severity: 'error', message: `Relocation order ${job.orderId}: ${result.error} Use Print last relocation order to retry the last order.` });
        else if (outcome && outcome.method !== 'printer') notify({ severity: 'warning', message: `Relocation order ${job.orderId}: ${outcome.method === 'savePdf' ? 'PDF download requested.' : 'Printing skipped.'}${outcome.reason ? ` ${outcome.reason}` : ''}` });
    };
    const feedback = <>
        {job && <RelocationOrderPrintJob key={job.key} order={job} onComplete={complete} />}
    </>;
    return { autoPrint, setAutoPrint: (checked: boolean) => setSetting(autoPrintKey, checked), lastOrder, printing: jobs.length > 0, printLast, onRelocated, feedback };
};

// These values are table inputs; keep them above the table hook, with workflow mechanics below it.
export const useRelocationRequestState = () => {
    const [refreshKey, setRefreshKey] = useState(0);
    const [job, setJob] = useState<{ id: number; request: AxiosRequestConfig; count: number } | null>(null);
    const [preparing, setPreparing] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const refreshRowsRef = useRef<unknown[] | null>(null);
    const savingRef = useRef(false);
    return { refreshKey, setRefreshKey, job, setJob, preparing, setPreparing, refreshing, setRefreshing, refreshRowsRef, savingRef };
};

type Identifier = string | number;

type RelocationOrder = { orderId: Identifier; warehouseId: Identifier };

type RelocationPrintResult = { outcome?: PrintOutcome; error?: string };

type RelocationPrintJob = RelocationOrder & { key: number; settings: DispatchPrintingSettings };

const hasIdentifier = (value: unknown): value is Identifier => (typeof value === 'string' && value.trim() !== '') || (typeof value === 'number' && Number.isFinite(value));

export const RelocationOrderPrintControls = ({ printing, disabled = false, showPrintLast = true, showAutoPrint = true }: { printing: ReturnType<typeof useRelocationOrderPrinting>; disabled?: boolean; showPrintLast?: boolean; showAutoPrint?: boolean }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {showPrintLast && <CustomButton variant="outlined" icon={<PrintIcon />} title="Print last relocation order" tooltipText={printing.lastOrder ? 'Print last relocation order' : 'Save a relocation to print its order'} disabled={disabled || printing.printing || !printing.lastOrder} onClick={printing.printLast} />}
        {showAutoPrint && <Tooltip title="Auto-Print Relocation Order" describeChild arrow><Box sx={{ width: 42, flexShrink: 0 }}><FormField fieldProps={{ name: 'AutoPrint', label: 'Auto-Print Relocation Order', checkbox: true, xs: 12, disabled }} sx={{ m: 0, '& .MuiFormControlLabel-label': visuallyHidden }} formikProps={{ values: { AutoPrint: printing.autoPrint }, handleChange: event => printing.setAutoPrint(event.target.checked) }} /></Box></Tooltip>}
    </Box>
);

// A fresh request for every print/reprint; receive the endpoint's PDF as bytes.
const RelocationOrderPrintJob = ({ order, onComplete }: { order: RelocationPrintJob; onComplete: (result: RelocationPrintResult) => void }) => {
    const [response, setRequest, , loading, status] = useRequest({ logLevel: [] });
    const startRef = useRef(() => setRequest({ ...printRelocationOrder(order.warehouseId, order.orderId), responseType: 'blob' }));
    const started = useRef(false);
    const claimed = useRef(false);
    const mounted = useRef(false);
    const completeRef = useRef(onComplete);
    completeRef.current = onComplete;
    useEffect(() => {
        mounted.current = true;
        if (!started.current) { started.current = true; startRef.current(); }
        return () => { mounted.current = false; };
    }, []);
    useEffect(() => {
        if (claimed.current || loading) return;
        const state: unknown = status;
        const succeeded = state === 'success' || (typeof state === 'number' && state >= 200 && state < 300);
        const failed = state === 'error' || state === 'aborted' || (typeof state === 'number' && state >= 400);
        if (!succeeded && !failed) return;
        claimed.current = true;
        if (failed || state === 204 || response == null) {
            completeRef.current({ error: `Could not load the PDF (${state}).` });
            return;
        }
        printDispatchPdf(response, order.settings, 'relocationOrder', `Relocation order - ${order.orderId}`)
            .then((outcome) => { if (mounted.current) completeRef.current({ outcome }); })
            .catch((error) => { if (mounted.current) completeRef.current({ error: error instanceof Error ? error.message : String(error) }); });
    }, [response, loading, status, order]);
    return null;
};

// Each attempt mounts a fresh request hook. A successful empty response still
// completes the move, and a previous attempt cannot finish a later request.
export const RelocationRequest = ({ request, onComplete }: { request: AxiosRequestConfig; onComplete: (succeeded: boolean, response: unknown) => void }) => {
    const [response, setRequest, , loading, status] = useRequest({ logLevel: ['error', 'success'] });
    const startRef = useRef(() => setRequest(request));
    const startedRef = useRef(false);
    const completeRef = useRef(onComplete);
    const handledRef = useRef(false);
    completeRef.current = onComplete;
    useEffect(() => { if (!startedRef.current) { startedRef.current = true; startRef.current(); } }, []);
    useEffect(() => {
        const state: unknown = status;
        if (handledRef.current || loading) return;
        const succeeded = state === 'success' || (typeof state === 'number' && state >= 200 && state < 300);
        const failed = state === 'error' || state === 'aborted' || (typeof state === 'number' && state >= 400);
        if (!succeeded && !failed) return;
        handledRef.current = true;
        completeRef.current(succeeded, response);
    }, [response, loading, status]);
    return null;
};
